import type { MouseEvent, ReactNode } from 'react';
import styles from './DialogFrame.module.css';

type DialogSize = 'sm' | 'md' | 'lg';

interface DialogFrameProps {
  isOpen: boolean;
  title?: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose?: () => void;
  size?: DialogSize;
  ariaLabelledBy?: string;
  closeOnOverlayClick?: boolean;
  hideCloseButton?: boolean;
}

const sizeClassMap: Record<DialogSize, string> = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
  lg: styles.sizeLg,
};

export function DialogFrame({
  isOpen,
  title,
  description,
  children,
  footer,
  onClose,
  size = 'md',
  ariaLabelledBy = 'dialog-title',
  closeOnOverlayClick = true,
  hideCloseButton = false,
}: DialogFrameProps) {
  if (!isOpen) return null;

  const handleRootClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!closeOnOverlayClick) return;
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  return (
    <div
      className={styles.modal}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? ariaLabelledBy : undefined}
      onClick={handleRootClick}
    >
      <div className={styles.overlay} aria-hidden="true" />
      <div className={`${styles.content} ${sizeClassMap[size]}`} onClick={(e) => e.stopPropagation()}>
        {!hideCloseButton && onClose ? (
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close dialog"
          >
            ×
          </button>
        ) : null}
        {title || description ? (
          <div className={styles.header}>
            {title ? (
              <h2 id={ariaLabelledBy} className={styles.title}>
                {title}
              </h2>
            ) : null}
            {description ? <p className={styles.description}>{description}</p> : null}
          </div>
        ) : null}
        <div className={styles.body}>{children}</div>
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>
  );
}
